import { ListSection } from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    getEntitlementPlanLabel,
    getEntitlementStatusDescription,
    getEntitlementStatusLabel,
    getEntitlementStatusTone,
    type EntitlementStatusTone,
} from '@/lib/entitlements';
import { cn } from '@/lib/utils';
import QuotaUsageTable from './QuotaUsageTable';
import type { Entitlements } from '@/api/entitlements';

type EntitlementsOverviewProps = {
    entitlements?: Entitlements | null;
    isLoading: boolean;
    isError: boolean;
    showPlanSummary?: boolean;
    onUpgradeClick?: () => void;
    onManageSubscriptionClick?: () => void;
    isManageSubscriptionLoading?: boolean;
    className?: string;
};

const STATUS_BADGE_VARIANTS: Record<
    EntitlementStatusTone,
    'success' | 'warning' | 'destructive' | 'info' | 'neutral'
> = {
    success: 'success',
    warning: 'warning',
    destructive: 'destructive',
    info: 'info',
    neutral: 'neutral',
};

const getUpgradeLabel = (entitlements?: Entitlements | null) => {
    if (!entitlements || !entitlements.active) return 'Ver planos';
    if (entitlements.plan === 'PRO') return 'Ver planos';
    return 'Melhorar plano';
};

export default function EntitlementsOverview({
    entitlements,
    isLoading,
    isError,
    showPlanSummary = true,
    onUpgradeClick,
    onManageSubscriptionClick,
    isManageSubscriptionLoading = false,
    className,
}: EntitlementsOverviewProps) {
    const statusTone = getEntitlementStatusTone(entitlements);
    const statusVariant = STATUS_BADGE_VARIANTS[statusTone];
    const planLabel = entitlements?.name?.trim() || getEntitlementPlanLabel(entitlements?.plan);
    const statusLabel = getEntitlementStatusLabel(entitlements?.status);
    const statusDescription = getEntitlementStatusDescription(entitlements);
    const actions =
        onManageSubscriptionClick || onUpgradeClick ? (
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                {onManageSubscriptionClick ? (
                    <Button
                        variant="outline"
                        size="lg"
                        className="h-12 rounded-full px-7 text-base"
                        onClick={onManageSubscriptionClick}
                        disabled={isManageSubscriptionLoading}
                    >
                        {isManageSubscriptionLoading ? 'Redirecionando...' : 'Gerenciar Assinatura'}
                    </Button>
                ) : null}
                {onUpgradeClick ? (
                    <Button
                        size="lg"
                        className="h-12 rounded-full px-8 text-base"
                        onClick={onUpgradeClick}
                    >
                        {getUpgradeLabel(entitlements)}
                    </Button>
                ) : null}
            </div>
        ) : null;

    return (
        <div className={cn('space-y-6', className)}>
            {showPlanSummary ? (
                <div className="relative overflow-hidden rounded-4xl border border-border/80 bg-card p-6 shadow-elevation-1">
                    <div className="relative">
                        {isLoading ? (
                            <div className="space-y-4">
                                <Skeleton className="h-6 w-40" />
                                <Skeleton className="h-10 w-56" />
                                <Skeleton className="h-5 w-full max-w-2xl" />
                                <Skeleton className="h-10 w-40 rounded-full" />
                            </div>
                        ) : isError ? (
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div className="flex-1 rounded-2xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
                                    Não foi possível carregar os detalhes do seu plano agora.
                                </div>
                                {actions}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                                <div className="max-w-3xl space-y-4">
                                    <Badge
                                        variant={statusVariant}
                                        className="w-fit rounded-full px-3 py-1"
                                    >
                                        {statusLabel}
                                    </Badge>
                                    <div>
                                        <h2 className="mt-1 text-3xl font-bold tracking-tight text-foreground">
                                            {planLabel}
                                        </h2>
                                    </div>
                                    <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
                                        {statusDescription}
                                    </p>
                                </div>
                                {actions}
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            <ListSection
                title="Limites do plano"
                description="Acompanhe o consumo dos recursos que possuem cota no seu ciclo atual."
                contentClassName="p-0"
            >
                <QuotaUsageTable
                    entitlements={entitlements}
                    isLoading={isLoading}
                    isError={isError}
                />
            </ListSection>
        </div>
    );
}
